/**
 * Утилиты для работы с иконками новых метабулитов (TextEntity + Marker)
 */

import { Canvas, FabricObject } from 'fabric';

import { TextEntity } from '../../metaBullets/classes/TextEntity';
import { IconHub } from '../../store/media-hub';
import { scaleImage } from '../imageUtils';

/**
 * Проверяет, есть ли у TextEntity маркер с контентом-изображением
 */
export const hasTextEntityMarkerWithImage = (
  canvas: Canvas,
  entityId: string,
): { hasImage: boolean; marker: FabricObject | null; imageContent: FabricObject | null } => {
  const allObjects = canvas.getObjects();

  // Находим маркер для данной TextEntity
  const marker = allObjects.find(
    (obj: unknown) =>
      (obj as FabricObject).type === 'marker' &&
      (obj as Record<string, unknown>).isMarker &&
      (obj as Record<string, unknown>).entityId === entityId,
  );

  if (!marker) {
    return { hasImage: false, marker: null, imageContent: null };
  }

  // Находим контент-объекты маркера
  const contentObjects = allObjects.filter(
    (obj: unknown) => (obj as Record<string, unknown>).markerId === marker.id,
  );

  // Ищем изображение среди контента
  const imageContent = contentObjects.find(
    (obj: unknown) => (obj as FabricObject).type === 'image',
  );

  return {
    hasImage: !!imageContent,
    marker,
    imageContent: imageContent || null,
  };
};

/**
 * Проверяет, есть ли у Marker контент-изображение
 */
export const hasMarkerImage = (
  canvas: Canvas,
  markerId: string,
): { hasImage: boolean; imageContent: FabricObject | null } => {
  const allObjects = canvas.getObjects();

  // Находим контент-объекты маркера
  const contentObjects = allObjects.filter(
    (obj: unknown) => (obj as Record<string, unknown>).markerId === markerId,
  );

  // Ищем изображение среди контента
  const imageContent = contentObjects.find(
    (obj: unknown) => (obj as FabricObject).type === 'image',
  );

  return {
    hasImage: !!imageContent,
    imageContent: imageContent || null,
  };
};

/**
 * Получает available_images для новых метабулитов
 * Проверяет контент-объект и общие objects с category icons/photos/objects
 */
export const getAvailableImagesForNewBullet = (
  canvas: Canvas,
  targetId: string,
  isMarker: boolean = false,
): IconHub[] => {
  console.debug('🔍 [getAvailableImagesForNewBullet] Starting:', {
    targetId,
    isMarker,
  });

  const allObjects = canvas.getObjects();

  // Для диагностики проверяем что content object существует
  if (isMarker) {
    const { imageContent } = hasMarkerImage(canvas, targetId);
    console.debug('🔍 [getAvailableImagesForNewBullet] Marker path:', {
      hasImageContent: !!imageContent,
      imageContentId: imageContent?.id,
    });
  } else {
    const { imageContent } = hasTextEntityMarkerWithImage(canvas, targetId);
    console.debug('🔍 [getAvailableImagesForNewBullet] TextEntity path:', {
      hasImageContent: !!imageContent,
      imageContentId: imageContent?.id,
    });
  }

  // Для новых метабулитов ВСЕГДА ищем только ICONS
  // (photos/objects не поддерживаются для новых метабулитов)
  const targetCategory = 'icons';

  console.debug('🔍 [getAvailableImagesForNewBullet] Looking for icons:', {
    targetCategory,
  });

  // Ищем объект с category: "icons" на canvas
  const categoryObject = allObjects.find((obj: unknown) => {
    const objCategory = (obj as Record<string, unknown>).category as string | undefined;
    return objCategory?.toLowerCase() === targetCategory.toLowerCase();
  });

  console.debug('🔍 [getAvailableImagesForNewBullet] Category object found:', {
    found: !!categoryObject,
    categoryObjectId: categoryObject ? (categoryObject as FabricObject).id : null,
    categoryObjectType: categoryObject ? (categoryObject as FabricObject).type : null,
    hasAvailableImages: categoryObject
      ? Array.isArray((categoryObject as unknown as Record<string, unknown>).available_images)
      : false,
    availableImagesCount:
      categoryObject &&
      Array.isArray((categoryObject as unknown as Record<string, unknown>).available_images)
        ? ((categoryObject as unknown as Record<string, unknown>).available_images as IconHub[])
            .length
        : 0,
  });

  // Собираем available_images ТОЛЬКО из category object
  const availableImages: IconHub[] = [];

  if (
    categoryObject &&
    Array.isArray((categoryObject as unknown as Record<string, unknown>).available_images)
  ) {
    availableImages.push(
      ...((categoryObject as unknown as Record<string, unknown>).available_images as IconHub[]),
    );

    console.debug('✅ [getAvailableImagesForNewBullet] Icons loaded from category object:', {
      count: availableImages.length,
    });
  } else {
    console.warn('❌ [getAvailableImagesForNewBullet] No icons found!', {
      categoryObjectExists: !!categoryObject,
      hasAvailableImages: categoryObject
        ? Array.isArray((categoryObject as unknown as Record<string, unknown>).available_images)
        : false,
    });
  }

  console.debug('🎯 [NewBullet] Final result:', {
    targetId,
    isMarker,
    totalImages: availableImages.length,
  });

  return availableImages;
};

/**
 * Получает ID TextEntity для маркера
 */
export const getEntityIdForMarker = (canvas: Canvas, markerId: string): string | null => {
  const allObjects = canvas.getObjects();
  const marker = allObjects.find((obj: unknown) => (obj as FabricObject).id === markerId);

  if (!marker || (marker as FabricObject).type !== 'marker') {
    return null;
  }

  return (marker as unknown as Record<string, unknown>).entityId as string | null;
};

/**
 * Проверяет, является ли объект TextEntity с маркером-изображением
 */
export const isTextEntityWithMarkerImage = (canvas: Canvas, obj: FabricObject): boolean => {
  if (!TextEntity.isTextEntity(obj)) {
    return false;
  }

  const { hasImage } = hasTextEntityMarkerWithImage(canvas, obj.id);
  return hasImage;
};

/**
 * Проверяет, является ли объект Marker с изображением
 */
export const isMarkerWithImage = (canvas: Canvas, obj: FabricObject): boolean => {
  console.debug('🔍 [isMarkerWithImage] Checking:', {
    objId: obj.id,
    objType: obj.type,
    isMarkerType: obj.type === 'marker',
  });

  if (!obj || obj.type !== 'marker') {
    console.debug('❌ [isMarkerWithImage] Not a marker');
    return false;
  }

  const { hasImage, imageContent } = hasMarkerImage(canvas, obj.id);

  console.debug('🔍 [isMarkerWithImage] Result:', {
    markerId: obj.id,
    hasImage,
    imageContentId: imageContent?.id,
    imageContentType: imageContent?.type,
  });

  return hasImage;
};

/**
 * Заменяет иконку в новом метабулите
 * @param canvas - Fabric canvas
 * @param targetId - ID TextEntity или Marker
 * @param newIconSrc - URL нового изображения
 * @param sourceExt - Расширение файла
 * @param isMarker - true если targetId это Marker, false если TextEntity
 */
export const changeIconForNewBullet = async (
  canvas: Canvas,
  targetId: string,
  newIconSrc: string,
  sourceExt: string,
  isMarker: boolean = false,
): Promise<boolean> => {
  try {
    let imageContent: FabricObject | null = null;

    if (isMarker) {
      // Если выбран маркер - находим его content image
      const result = hasMarkerImage(canvas, targetId);
      imageContent = result.imageContent;
    } else {
      // Если выбран TextEntity - находим маркер и его content image
      const result = hasTextEntityMarkerWithImage(canvas, targetId);
      imageContent = result.imageContent;
    }

    if (!imageContent) {
      console.error('🔴 [ChangeIcon] Image content not found:', { targetId, isMarker });
      return false;
    }

    console.debug('🔄 [ChangeIcon] Replacing icon for new bullet:', {
      targetId,
      isMarker,
      oldImageId: imageContent.id,
      newIconSrc,
      sourceExt,
    });

    // Сохраняем параметры старого изображения
    const oldWidth = imageContent.width! * (imageContent.scaleX || 1);
    const oldHeight = imageContent.height! * (imageContent.scaleY || 1);
    const maxSize = Math.max(oldWidth, oldHeight);

    const imageColor = (imageContent as unknown as Record<string, unknown>).imageColor as
      | string
      | undefined;

    // Заменяем {color} если есть
    let finalSrc = newIconSrc;
    if (finalSrc.includes('{color}')) {
      finalSrc = finalSrc.replace('{color}', imageColor ?? '');
    }

    // Динамический импорт Image из fabric
    const { Image: FabricImage } = await import('fabric');

    // Загружаем новое изображение
    const newImg = await FabricImage.fromURL(finalSrc, {
      crossOrigin: 'anonymous',
    });

    const imgToPasteWidth = newImg._originalElement.width;
    const imgToPasteHeight = newImg._originalElement.height;

    const scaleImgToPaste = Math.min(maxSize / imgToPasteWidth, maxSize / imgToPasteHeight);

    // Масштабируем новое изображение используя существующую утилиту
    await scaleImage(newImg, imgToPasteWidth * scaleImgToPaste, imgToPasteHeight * scaleImgToPaste);

    const oldImageProps = imageContent as unknown as Record<string, unknown>;

    // Копируем параметры со старого изображения
    const markerId = oldImageProps.markerId as string;
    newImg.set({
      id: imageContent.id, // Сохраняем ID
      left: imageContent.left,
      top: imageContent.top,
      originX: imageContent.originX,
      originY: imageContent.originY,
      markerId: markerId, // ✅ Связь с маркером
      category: oldImageProps.category,
      available_images: oldImageProps.available_images,
      source_ext: sourceExt,
      imageColor: oldImageProps.imageColor,
      media_type: oldImageProps.media_type,
      inner_id: oldImageProps.inner_id,
      zIndex: oldImageProps.zIndex,
      source_type: oldImageProps.source_type,
      noCopy: oldImageProps.noCopy,
      layer: oldImageProps.layer,
      _markerOffsetX: oldImageProps._markerOffsetX,
      // ВАЖНО: offsetY всегда 0 - контент всегда на одной вертикали с центром маркера
      _markerOffsetY: 0,
    });

    console.debug('🔄 [ChangeIcon] Updating marker contentObjects:', {
      markerId,
      oldImageId: imageContent.id,
      newImageId: newImg.id,
    });

    // ✅ КРИТИЧНО: Обновляем массив contentObjects в маркере
    // Находим маркер
    const markerObject = canvas.getObjects().find((obj) => obj.id === markerId);
    const markerProps = markerObject as unknown as Record<string, unknown>;

    if (markerObject && markerProps.isMarker) {
      console.debug('✅ [ChangeIcon] Found marker, updating contentObjects');

      // Удаляем старое изображение из маркера
      if (typeof markerProps.removeContentObject === 'function') {
        (markerProps.removeContentObject as (obj: FabricObject) => void)(imageContent);
      }

      // Удаляем старое изображение с canvas
      canvas.remove(imageContent);

      // Добавляем новое изображение на canvas (БЕЗ add в marker - он сам добавит)
      // НЕТ! marker.addContentObject сам добавит на canvas, НО только если его там нет
      // Поэтому добавляем сначала на canvas, потом в marker

      canvas.add(newImg);
      newImg.setCoords();

      // Добавляем новое изображение в маркер (это обновит позицию и заблокирует взаимодействие)
      if (typeof markerProps.addContentObject === 'function') {
        (markerProps.addContentObject as (obj: FabricObject) => void)(newImg);
      }

      console.debug('✅ [ChangeIcon] Marker contentObjects updated successfully');
    } else {
      console.warn('⚠️ [ChangeIcon] Marker not found or invalid:', {
        markerId,
        markerExists: !!markerObject,
        isMarker: markerObject ? markerProps.isMarker : false,
      });

      // Если маркер не найден, все равно добавляем изображение
      canvas.remove(imageContent);
      canvas.add(newImg);
      newImg.setCoords();
    }

    console.debug('✅ [ChangeIcon] Icon replaced successfully:', {
      targetId,
      newImageId: newImg.id,
      markerId: (newImg as unknown as Record<string, unknown>).markerId,
    });

    return true;
  } catch (error) {
    console.error('🔴 [ChangeIcon] Error changing icon:', error);
    return false;
  }
};
